// Shared count of un-actioned clinic-access requests. Drives the sidebar
// "Notifications" red-dot badge and is refreshed after a decision. Only polls
// for users who can manage clinic access (Supervisor tier and up); a
// pass-through zero for everyone else.

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../auth/AuthContext.jsx';
import { getClinicRequestsCount } from '../api/client.js';

const POLL_MS = 60_000;
const Ctx = createContext({ count: 0, refresh: () => {} });

export function ClinicApprovalsProvider({ children }) {
  const { user } = useAuth();
  const enabled = !!user?.canManageClinicAccess;
  const [count, setCount] = useState(0);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    if (!enabled) { setCount(0); return; }
    try { const { count } = await getClinicRequestsCount(); setCount(count || 0); }
    catch { /* leave the last known count; a transient error must not clear the badge */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) { setCount(0); return; }
    refresh();
    timer.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer.current);
  }, [enabled, refresh]);

  // setCount lets the open inbox page keep the badge exactly in step with the
  // list it just fetched (no count-vs-list drift while viewing).
  return <Ctx.Provider value={{ count, refresh, setCount }}>{children}</Ctx.Provider>;
}

export function useClinicApprovals() { return useContext(Ctx); }
